import { Modal, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import React from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  CreateSOPMappingDTO,
  SOPMappingQueryDTO,
  UpdateSOPMappingDTO,
} from "@/modules/sop/domain/contracts/mapping";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useCreateSOPMapping,
  useDeleteSOPMapping,
  useSOPMappings,
  useUpdateSOPMapping,
} from "../api/queries";
import { SOPMappingFilter } from "../ui/SOPMappingFilter";
import { SOPMappingForm } from "../ui/SOPMappingForm";
import { SOPMappingTable } from "../ui/SOPMappingTable";
import { SOPMappingToolbar } from "../ui/SOPMappingToolbar";

export function SOPMappingPage() {
  const nav = useNavigate({ from: "/app/sop-mapping" });
  const search: Partial<SOPMappingQueryDTO> = useSearch({
    from: "/app/sop-mapping",
  });

  const list = useSOPMappings({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
    sopId: search.sopId,
    positionId: search.positionId,
    departmentId: search.departmentId,
    search: search.search,
  });

  const canManage = useActionPermission(["sop:create"]);
  const createMapping = useCreateSOPMapping();
  const updateMapping = useUpdateSOPMapping();
  const deleteMapping = useDeleteSOPMapping();

  const {
    isOpen: isCreateOpen,
    open: onCreateOpen,
    close: onCreateClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    open: onEditOpen,
    close: onEditClose,
  } = useDisclosure();
  const [editingMapping, setEditingMapping] = React.useState<
    ({ id: string } & Partial<CreateSOPMappingDTO>) | null
  >(null);

  const handleCreate = (values: CreateSOPMappingDTO) => {
    createMapping.mutate(values, {
      onSuccess: () => {
        onCreateClose();
        list.refetch();
      },
    });
  };

  const handleEdit = (mapping: {
    id: string;
    sopId: string;
    positionId: string;
    departmentId?: string | null;
  }) => {
    setEditingMapping({
      id: mapping.id,
      sopId: mapping.sopId,
      positionId: mapping.positionId,
      departmentId: mapping.departmentId || undefined,
    });
    onEditOpen();
  };

  const handleUpdate = (values: UpdateSOPMappingDTO) => {
    if (!editingMapping) return;
    updateMapping.mutate(
      { id: editingMapping.id, input: values },
      {
        onSuccess: () => {
          onEditClose();
          setEditingMapping(null);
          list.refetch();
        },
      },
    );
  };

  const handleDelete = async (mapping: { id: string }) => {
    await toast.promise(deleteMapping.run(mapping.id), {
      loading: "ກຳລັງລຶບ...",
      success: "ລຶບການຜູກ SOP ສຳເລັດ",
      error: "ລຶບການຜູກ SOP ລົ້ມເຫຼວ",
    });
    list.refetch();
  };

  const handleFiltersChange = (newFilters: Partial<SOPMappingQueryDTO>) => {
    nav({
      search: {
        ...search,
        ...newFilters,
        offset: 0, // Reset to first page when filters change
      },
    });
  };

  return (
    <>
      <Header />
      <Main>
        <SOPMappingToolbar canManage={!!canManage} onCreate={onCreateOpen} />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <SOPMappingFilter
            filters={search}
            onFiltersChange={handleFiltersChange}
          />

          <SOPMappingTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>

        <Modal
          open={isCreateOpen}
          onOpenChange={(open) => {
            if (!open) onCreateClose();
          }}
          title="ສ້າງການຜູກ SOP ໃໝ່"
        >
          <SOPMappingForm
            onSubmit={handleCreate}
            submitting={createMapping.isPending}
          />
        </Modal>

        <Modal
          open={isEditOpen}
          onOpenChange={(open) => {
            if (!open) {
              onEditClose();
              setEditingMapping(null);
            }
          }}
          title="ແກ້ໄຂການຜູກ SOP"
        >
          {editingMapping && (
            <SOPMappingForm
              initialValues={editingMapping}
              onSubmit={handleUpdate}
              submitting={updateMapping.isPending}
            />
          )}
        </Modal>
      </Main>
    </>
  );
}
